import { EmptyState, ErrorState, Loading } from "@uzverse/ui";
import { useTranslation } from "react-i18next";
import { useCommonT } from "../components/common.js";
import { useSession } from "../state/session.js";
import { useLoad } from "../state/useLoad.js";
import { BUILTIN_CREDITS } from "./builtinCredits.js";

/** Where the text of each license the world accepts lives. */
const LICENSE_URLS: Record<string, string> = {
  "CC0-1.0": "https://creativecommons.org/publicdomain/zero/1.0/",
  "CC-BY-4.0": "https://creativecommons.org/licenses/by/4.0/",
  MIT: "https://opensource.org/license/mit",
  "MIT-0": "https://opensource.org/license/mit-0",
  Unlicense: "https://unlicense.org/",
};

export const licenseUrl = (spdx: string): string | null => LICENSE_URLS[spdx] ?? null;

/** Only http(s) links are shown as links: the source page comes from an agent's claim. */
const safeHref = (url: string): string | null => {
  try {
    const u = new URL(url);
    return u.protocol === "https:" || u.protocol === "http:" ? u.toString() : null;
  } catch {
    return null;
  }
};

/**
 * The credits page: every approved third-party 3D asset with its author, license and source
 * (CC-BY and MIT ask for this). The list is the world's approved assets, nothing else.
 */
export function CreditsPage() {
  const { t } = useTranslation("web");
  const tc = useCommonT();
  const { services } = useSession();
  const credits = useLoad(() => services.api.listCredits());
  const rows = credits.data?.credits;

  return (
    <section aria-labelledby="credits-title" className="page">
      <header className="page-head">
        <div>
          <h1 id="credits-title">{t("credits.title")}</h1>
          <p className="muted">{t("credits.intro")}</p>
        </div>
        {rows && rows.length > 0 ? (
          <span className="chip" data-testid="credits-count">
            {t("credits.count", { count: rows.length })}
          </span>
        ) : null}
      </header>
      {credits.error ? (
        <ErrorState t={tc} message={t("credits.unavailable")} onRetry={credits.reload} />
      ) : !rows ? (
        <Loading t={tc} />
      ) : rows.length === 0 ? (
        <EmptyState t={tc} title={t("credits.empty")} />
      ) : (
        <ul className="plain stack credits-list">
          {rows.map((c) => {
            const href = safeHref(c.sourceUrl);
            const lic = licenseUrl(c.license);
            return (
              <li key={c.sha256} className="card credit" data-testid="credit">
                <div className="row">
                  <strong className="grow">{c.name}</strong>
                  <span className="chip">{c.license}</span>
                </div>
                <p className="small">
                  {c.author ? t("credits.by", { author: c.author }) : t("credits.unknownAuthor")}
                </p>
                <p className="muted small">
                  {c.attribution ?? (c.attributionRequired ? "" : t("credits.noAttribution"))}
                </p>
                <p className="small credit-links">
                  {href ? (
                    <a className="link" href={href} target="_blank" rel="noreferrer noopener">
                      {t("credits.source")}
                    </a>
                  ) : null}
                  {lic ? (
                    <a className="link" href={lic} target="_blank" rel="noreferrer noopener">
                      {t("credits.licenseLink", { license: c.license })}
                    </a>
                  ) : null}
                </p>
              </li>
            );
          })}
        </ul>
      )}
      <section
        aria-labelledby="builtin-credits-title"
        className="stack"
        data-testid="builtin-credits"
      >
        <h2 id="builtin-credits-title">{t("credits.builtinTitle")}</h2>
        <p className="muted">{t("credits.builtinIntro")}</p>
        <ul className="plain stack credits-list">
          {BUILTIN_CREDITS.map((c) => (
            <li key={c.id} className="card credit">
              <div className="row">
                <strong className="grow">{c.name}</strong>
                <span className="chip">{c.license}</span>
              </div>
              <p className="small">{t("credits.by", { author: c.author })}</p>
              <p className="muted small">{c.use}</p>
              <p className="small credit-links">
                <a className="link" href={c.source} target="_blank" rel="noreferrer noopener">
                  {t("credits.source")}
                </a>
                <a
                  className="link"
                  href={licenseUrl(c.license) ?? undefined}
                  target="_blank"
                  rel="noreferrer noopener"
                >
                  {t("credits.licenseLink", { license: c.license })}
                </a>
              </p>
            </li>
          ))}
        </ul>
      </section>
    </section>
  );
}
